// ─── AR Reconciliation – Access & Hospital helpers ─────────────────────────────
// Centralised so both the Header tab and the page route-guard stay in sync.

// Roles that grant access to AR Reconciliation.
export const AR_ACCESS_ROLE = 'ar_dash';
export const AR_VIEW_ROLE = 'ar_dash_view';
export const AR_ROLES = [AR_ACCESS_ROLE, AR_VIEW_ROLE];

// Mobile-based role mapping for AR Reconciliation
export const AR_ROLE_BASED_MOBILES = {
  '8600062361': 'ar_dash',
  '9986943464': 'ar_dash',
  '9921782780': 'ar_dash',
  '9819297360': 'ar_dash',
  '9022841188': 'ar_dash',
  '6302278076': 'ar_dash',
  '9015112729': 'ar_dash',
  '8882907911': 'ar_dash',
  '9582157997': 'ar_dash',
  '9667069572': 'ar_dash',
  '9811130032': 'ar_dash',
  '9121661476': 'ar_dash',
  '9384471032': 'ar_dash',
  '8248301322': 'ar_dash',
  '8220142004': 'ar_dash',
  '9952083734': 'ar_dash',
  '9844303498': 'ar_dash',
  '7893050403': 'ar_dash',
  '9296511421': 'ar_dash',
  '8210499935': 'ar_dash',
  '8114388610': 'ar_dash',
  '9090963722': 'ar_dash',
  '9807424395': 'ar_dash',
  '7389285956': 'ar_dash',
  '8699116698': 'ar_dash',
  '8271045374': 'ar_dash',
  '8090099330': 'ar_dash',
  '9431274600': 'ar_dash',
  '8121688779': 'ar_dash',
  '7047591224': 'ar_dash',
  '7007929299': 'ar_dash_view',
  '8104937369': 'ar_dash_view',
  '9289392643': 'ar_dash_view',
  '9990368989': 'ar_dash_view',
  '9999067465': 'ar_dash_view',
  '9384703493': 'ar_dash_view',
  '6385452420': 'ar_dash_view',
  '9606562041': 'ar_dash_view',
  '8249859881': 'ar_dash_view',
  '9580330344': 'ar_dash_view',
  '7737950522': 'ar_dash_view',
  '8011825755': 'ar_dash_view',
  '9341799179': 'ar_dash_view',
  '8297841823': 'ar_dash_view',
};

// User-specific restricted hospitals (e.g. mobile 8699116698 -> only 2 hospitals)
export const MOBILE_RESTRICTED_HOSPITALS = {
  '8699116698': [
    { id: 'RCo2M2alh4', name: 'Kare Partner Hospital - Chandigarh' },
    { id: 'oUnE0gnxXe', name: 'KARE Partner - Punchkula' },
  ],
};

const TEMP_WHITELIST_EMAILS = [
  'sunny.sen@pristyncare.com',
  'rohit.kumar6@pristyncare.com',
  'md.arif@pristyncare.com',
];

const TEMP_WHITELIST_USERNAMES = [
  'sunny.sen',
  'rohit.kumar6',
  'md.arif',
];

const normalize = (v) => (v == null ? '' : String(v).trim().toLowerCase());

const getUserEmail = (user) =>
  normalize(user?.email || user?.emailId || user?.Email || user?.email_id);

const getUserName = (user) =>
  normalize(user?.username || user?.user_name || user?.userName || user?.name || user?.userId);

const isWhitelisted = (user) =>
  TEMP_WHITELIST_EMAILS.includes(getUserEmail(user)) ||
  TEMP_WHITELIST_USERNAMES.includes(getUserName(user));

export const getUserMobile = (user) => {
  const raw = String(
    user?.otpMobile ||
    user?.mobile ||
    user?.mobileNumber ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('userMobile') : '') ||
    ''
  ).trim();
  const mobile = raw.slice(-10);
  return mobile || raw;
};

export function getUserRestrictedHospitals(user) {
  const mobile = getUserMobile(user);
  if (mobile && MOBILE_RESTRICTED_HOSPITALS[mobile]) {
    return MOBILE_RESTRICTED_HOSPITALS[mobile];
  }
  return null;
}

const getMobileRole = (user) => {
  const raw = String(user?.otpMobile || (typeof localStorage !== 'undefined' ? localStorage.getItem('userMobile') : '') || '').trim();
  const mobile = raw.slice(-10);
  return AR_ROLE_BASED_MOBILES[mobile] || AR_ROLE_BASED_MOBILES[raw] || null;
};

/**
 * True when the user may access AR Reconciliation.
 * Checks mobile mapping first, then whitelist (email/username), then user.roles.
 */
export function hasArAccess(user) {
  if (!user) return false;

  // 1. Mobile-based role check (user.otpMobile or localStorage userMobile)
  if (getMobileRole(user)) return true;

  // 2. TEMP whitelist bypass (by email / username)
  if (isWhitelisted(user)) return true;

  // 3. User roles check
  const validRoles = AR_ROLES.map((r) => r.toLowerCase());
  const role = normalize(user?.roles?.role);
  if (validRoles.includes(role)) return true;

  const otherRoles = user?.roles?.otherRoles;
  if (Array.isArray(otherRoles)) {
    return otherRoles.some((r) => validRoles.includes(normalize(r)));
  }
  return false;
}

/**
 * True when the user only has view permission (`ar_dash_view`) and does NOT have full action permissions (`ar_dash`).
 */
export function isArViewOnly(user) {
  if (!user) return false;
  if (isWhitelisted(user)) return false;

  // 1. Mobile-based role check
  const mobileRole = getMobileRole(user);
  if (mobileRole) {
    return mobileRole.toLowerCase() === AR_VIEW_ROLE.toLowerCase();
  }

  // 2. User roles check
  const role = normalize(user?.roles?.role);
  const otherRoles = Array.isArray(user?.roles?.otherRoles)
    ? user.roles.otherRoles.map(normalize)
    : [];
  const allRoles = [role, ...otherRoles];

  const hasFullAccess = allRoles.includes(AR_ACCESS_ROLE.toLowerCase());
  const hasViewAccess = allRoles.includes(AR_VIEW_ROLE.toLowerCase());

  return hasViewAccess && !hasFullAccess;
}

/**
 * True when the user can perform write/dispute/sign-off actions (i.e. has ar_dash full access).
 */
export function canPerformArAction(user) {
  return hasArAccess(user) && !isArViewOnly(user);
}
